import { z } from 'zod'
import { patchOf } from './patch.ts'

/**
 * M1 client API (§8, §9, §11, §19.11). All paths are below /api/v1. IDs are UUIDv7, dates ISO 8601.
 * Request schemas are validated by the api; response schemas document the shape for the client.
 */

const id = z.uuid()
const isoDate = z.iso.date() // YYYY-MM-DD
const isoDateTime = z.iso.datetime({ offset: true })

/**
 * Boolean query parameter. `z.coerce.boolean()` turns the string "false" into `true` (Boolean("false")),
 * so `?includeDone=false` used to mean the opposite. Accepts real booleans and "true"/"false"/"1"/"0".
 */
export const queryBool = z.preprocess(
  (v) => (v === 'true' || v === '1' ? true : v === 'false' || v === '0' || v === '' ? false : v),
  z.boolean(),
)

export const ContainerType = z.enum(['project', 'area', 'resource'])
export const ContainerStatus = z.enum(['active', 'on_hold', 'done', 'archived'])
export const AiPolicy = z.enum(['default', 'local_only', 'no_ai'])
export const Role = z.enum(['owner', 'editor', 'commenter', 'viewer'])
export const NoteType = z.enum([
  'note',
  'meeting',
  'decision',
  'daily',
  'reference',
  'brief',
  'person',
  'agent_report',
  'weekly_review',
])
export const TaskStatus = z.enum(['inbox', 'todo', 'doing', 'waiting', 'done', 'cancelled'])

// ── Containers ────────────────────────────────────────────────────────────────────────────────
export const ContainerSummary = z.object({
  id,
  type: ContainerType,
  title: z.string(),
  slug: z.string(),
  icon: z.string().nullable(),
  colorToken: z.string().nullable(),
  status: ContainerStatus,
  visibility: z.enum(['private', 'shared']),
  aiPolicy: AiPolicy,
  parentAreaId: id.nullable(),
  clientName: z.string().nullable(),
  priorityWeight: z.number().int().min(1).max(5),
  targetDate: isoDate.nullable(),
  archivedAt: isoDateTime.nullable(),
  role: Role,
  counts: z.object({ notes: z.number().int(), openTasks: z.number().int() }),
  lastActivityAt: isoDateTime,
})
export type ContainerSummary = z.infer<typeof ContainerSummary>

/** ADR 0024: order of the page tree; manual = drag & drop */
export const PageSort = z.enum(['manual', 'title_asc', 'title_desc', 'created_asc', 'created_desc'])
export type PageSort = z.infer<typeof PageSort>

export const ContainerDetail = ContainerSummary.extend({
  description: z.string().nullable(),
  pageSort: PageSort,
  /** ADR 0024: new top-level pages land below this page (null = top level) */
  defaultPageParentId: id.nullable(),
  briefNoteId: id.nullable(),
  startDate: isoDate.nullable(),
  ownerId: id,
  members: z.array(z.object({ userId: id, name: z.string(), avatarUrl: z.string().nullable(), role: Role })),
})
export type ContainerDetail = z.infer<typeof ContainerDetail>

export const ListContainersQuery = z.object({
  type: ContainerType.optional(),
  /** default: everything except archived */
  includeArchived: queryBool.default(false),
  archivedOnly: queryBool.default(false),
})

/** Project wizard (§8): for projects a brief note (type 'brief', template) is created automatically. */
export const CreateContainer = z.object({
  id: id.optional(),
  type: ContainerType,
  title: z.string().trim().min(1).max(200),
  parentAreaId: id.nullable().optional(),
  targetDate: isoDate.nullable().optional(),
  startDate: isoDate.nullable().optional(),
  clientName: z.string().trim().max(200).nullable().optional(),
  description: z.string().max(5000).nullable().optional(),
  icon: z.string().max(32).nullable().optional(),
  aiPolicy: AiPolicy.default('default'),
  priorityWeight: z.number().int().min(1).max(5).default(3),
})
export type CreateContainer = z.infer<typeof CreateContainer>

export const UpdateContainer = patchOf(CreateContainer.omit({ id: true, type: true })).extend({
  status: z.enum(['active', 'on_hold']).optional(),
  pageSort: PageSort.optional(),
  defaultPageParentId: id.nullable().optional(),
})
export type UpdateContainer = z.infer<typeof UpdateContainer>

/** "Projekt abschließen" (§8): move selected notes into a resource (existing or new), then archive. */
export const CompleteProject = z.object({
  moveNoteIds: z.array(id).default([]),
  targetResourceId: id.optional(),
  newResourceTitle: z.string().trim().min(1).max(200).optional(),
})
export type CompleteProject = z.infer<typeof CompleteProject>

// ── Notes ─────────────────────────────────────────────────────────────────────────────────────
export const NoteSummary = z.object({
  id,
  containerId: id.nullable(),
  type: NoteType,
  title: z.string(),
  icon: z.string().nullable(),
  excerpt: z.string(),
  pinned: z.boolean(),
  isFavorite: z.boolean(),
  updatedAt: isoDateTime,
  createdAt: isoDateTime,
  deletedAt: isoDateTime.nullable(),
  /** ADR 0007: parent page in the same container (null = top level) */
  parentNoteId: id.nullable(),
  /** order among siblings, lower first (null for pages created before ADR 0007) */
  sortOrder: z.number().nullable(),
  /** number of (non-trashed) direct subpages */
  childCount: z.number().int(),
})
export type NoteSummary = z.infer<typeof NoteSummary>

export const NoteDetail = NoteSummary.extend({
  contentMd: z.string(),
  properties: z.record(z.string(), z.unknown()),
  visibilityOverride: z.enum(['inherit', 'private']),
  createdBy: id,
  container: z.object({ id, title: z.string(), type: ContainerType, aiPolicy: AiPolicy }).nullable(),
  /** false → open the editor read-only */
  writable: z.boolean(),
  /** breadcrumbs: root page first, direct parent last (readable ancestors only) */
  ancestors: z.array(z.object({ id, title: z.string() })),
})
export type NoteDetail = z.infer<typeof NoteDetail>

export const ListNotesQuery = z.object({
  containerId: id.optional(),
  inbox: queryBool.optional(),
  type: NoteType.optional(),
  trash: queryBool.default(false),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
  /** with containerId (or inbox): every page of the container as a flat list ordered for a tree
   * (parentNoteId + sortOrder), no pagination (max. 5000) */
  tree: queryBool.optional(),
  /** only direct subpages of this page */
  parentNoteId: id.optional(),
  /** default: newest first by creation date; 'updated' = recently edited (home); the trash is always by deletion */
  sort: z.enum(['created', 'updated']).optional(),
})

export const CreateNote = z.object({
  id: id.optional(), // client-generated UUIDv7 allowed (offline create, §10)
  containerId: id.nullable().default(null),
  type: NoteType.default('note'),
  title: z.string().max(300).default(''),
  markdown: z.string().max(1_000_000).optional(),
  /** default true: initial content from the type's template when no markdown is given */
  useTemplate: z.boolean().default(true),
  properties: z.record(z.string(), z.unknown()).default({}),
  /** create as subpage; the page lands in the parent's container (containerId is ignored then) */
  parentNoteId: id.nullable().optional(),
})
export type CreateNote = z.infer<typeof CreateNote>

export const UpdateNote = z.object({
  title: z.string().max(300).optional(),
  icon: z.string().max(32).nullable().optional(),
  type: NoteType.optional(),
  properties: z.record(z.string(), z.unknown()).optional(),
  pinned: z.boolean().optional(),
  visibilityOverride: z.enum(['inherit', 'private']).optional(),
  /** reparent (same as POST /notes/:id/move with parentNoteId) */
  parentNoteId: id.nullable().optional(),
})
export type UpdateNote = z.infer<typeof UpdateNote>

/**
 * Move a page (with its subtree). `containerId` moves to another container (top level there unless
 * `parentNoteId` is given); `parentNoteId` reparents (null = top level of the current container);
 * `beforeId`/`afterId` position it among its new siblings.
 */
export const MoveNote = z
  .object({
    containerId: id.nullable().optional(),
    parentNoteId: id.nullable().optional(),
    beforeId: id.optional(),
    afterId: id.optional(),
  })
  .refine((m) => Object.values(m).some((v) => v !== undefined), { message: 'Ziel fehlt.' })
export type MoveNote = z.infer<typeof MoveNote>

export const AppendToNote = z.object({
  markdown: z.string().min(1).max(200_000),
  position: z.union([z.enum(['end', 'start']), z.object({ afterBlockId: z.string() })]).default('end'),
})

export const Backlinks = z.object({
  links: z.array(
    z.object({
      kind: z.enum([
        'wikilink',
        'mention',
        'relates',
        'derived_from',
        'supersedes',
        'blocks',
        'about_person',
      ]),
      from: z.object({
        type: z.enum(['note', 'task', 'container']),
        id,
        title: z.string(),
        containerTitle: z.string().nullable(),
      }),
    }),
  ),
  /** notes whose text contains this note's title without linking to it */
  unlinked: z.array(z.object({ id, title: z.string(), snippet: z.string() })),
})
export type Backlinks = z.infer<typeof Backlinks>

export const NoteVersion = z.object({
  id,
  createdAt: isoDateTime,
  createdBy: id.nullable(),
  reason: z.string(),
})
export const NoteVersionDetail = NoteVersion.extend({ contentMd: z.string() })

// ── Tasks (basic in M1; full feature set in M2) ───────────────────────────────────────────────
export const Task = z.object({
  id,
  containerId: id.nullable(),
  title: z.string(),
  descriptionMd: z.string(),
  status: TaskStatus,
  priority: z.number().int().min(1).max(4),
  dueDate: isoDate.nullable(),
  doDate: isoDate.nullable(),
  completedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type Task = z.infer<typeof Task>

export const ListTasksQuery = z.object({
  containerId: id.optional(),
  inbox: queryBool.optional(),
  status: z.union([TaskStatus, z.literal('open')]).optional(), // open = not done/cancelled
  limit: z.coerce.number().int().min(1).max(500).default(200),
})

export const CreateTask = z.object({
  id: id.optional(),
  title: z.string().trim().min(1).max(500),
  containerId: id.nullable().default(null),
  descriptionMd: z.string().max(100_000).default(''),
  priority: z.number().int().min(1).max(4).default(3),
  dueDate: isoDate.nullable().optional(),
  doDate: isoDate.nullable().optional(),
  status: TaskStatus.optional(),
})
export const UpdateTask = patchOf(CreateTask.omit({ id: true }))

// ── Inbox & capture (§8) ──────────────────────────────────────────────────────────────────────
export const Capture = z.object({
  id: id.optional(), // client-generated for the offline queue (idempotent)
  text: z.string().trim().min(1).max(100_000),
  kind: z.enum(['note', 'task']).default('note'),
  /** `#slug` in the text also works; resolved server-side */
  containerId: id.nullable().optional(),
})
export type Capture = z.infer<typeof Capture>

export const InboxEntry = z.object({
  kind: z.enum(['note', 'task', 'suggestion']),
  id,
  title: z.string(),
  excerpt: z.string(),
  origin: z.enum(['capture', 'meeting', 'agent', 'classification', 'other']),
  createdAt: isoDateTime,
  /** M3: AI suggestion (container, type, confidence, rationale) */
  suggestion: z
    .object({
      containerId: id.nullable(),
      containerTitle: z.string().nullable(),
      confidence: z.number(),
      rationale: z.string(),
    })
    .nullable(),
})
export type InboxEntry = z.infer<typeof InboxEntry>

export const Triage = z.object({
  items: z
    .array(z.object({ kind: z.enum(['note', 'task', 'suggestion']), id }))
    .min(1)
    .max(200),
  /** move = into containerId · to_task = convert note → task · discard = soft delete / reject · accept = apply suggestion */
  action: z.enum(['move', 'to_task', 'discard', 'accept']),
  containerId: id.nullable().optional(),
})
export type Triage = z.infer<typeof Triage>

// ── Search & jump (§11.1) ─────────────────────────────────────────────────────────────────────
export const SearchQuery = z.object({
  /** supports "exakt", -ausschließen, typ:meeting, projekt:slug, vor:2026-09-01, nach:… */
  q: z.string().trim().min(1).max(500),
  para: ContainerType.optional(),
  containerId: id.optional(),
  type: NoteType.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  includeArchived: queryBool.default(false),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /** cross-encoder reranking (slow on CPU: search page/chat/MCP, not ⌘K typeahead) */
  rerank: z
    .enum(['true', 'false', '1', '0'])
    .transform((v) => v === 'true' || v === '1')
    .optional(),
})
export type SearchQuery = z.infer<typeof SearchQuery>

export const SearchHit = z.object({
  /** 'file' = project file (ADR 0017): id is the file id, url opens it in its project ·
   * 'source' = document of a Git source (ADR 0019): id is the document id, see `source` ·
   * 'meeting' = passage of a meeting transcript (ADR 0021): id is the meeting id, url jumps to `startMs` */
  kind: z.enum(['note', 'container', 'task', 'file', 'source', 'meeting']),
  id,
  title: z.string(),
  /** HTML-escaped text with <mark>…</mark> around matches */
  snippet: z.string(),
  noteType: NoteType.nullable(),
  container: z.object({ id, title: z.string(), type: ContainerType, archived: z.boolean() }).nullable(),
  updatedAt: isoDateTime,
  score: z.number(),
  url: z.string(),
  /** M3: the best matching chunk of a note (hybrid search) and its heading path */
  chunkId: id.nullable().optional(),
  headingPath: z.string().optional(),
  /** kind 'source': repo (owner/name) and file path – shown with the "Git" badge */
  source: z.object({ repo: z.string(), path: z.string() }).optional(),
  /** kind 'meeting': transcript position of the passage and the meeting date */
  meeting: z.object({ startMs: z.number().int(), startedAt: isoDateTime }).optional(),
})
export type SearchHit = z.infer<typeof SearchHit>
export const SearchResult = z.object({
  hits: z.array(SearchHit),
  total: z.number().int(),
  /** 'hybrid' = FTS + vectors (+ reranker), 'fts' = text only (embedder unavailable or not configured) */
  mode: z.enum(['fts', 'hybrid']).optional(),
  reranked: z.boolean().optional(),
  /** §11.3 step 5: the best hit is below the threshold → "nichts Belastbares gefunden" */
  weak: z.boolean().optional(),
})
export type SearchResult = z.infer<typeof SearchResult>

/** Compact title index for client-side ⌘K "Springen" (< 50 ms, §11.1). */
export const JumpIndex = z.object({
  containers: z.array(z.object({ id, title: z.string(), type: ContainerType, archived: z.boolean() })),
  notes: z.array(z.object({ id, title: z.string(), type: NoteType, containerId: id.nullable() })),
  tasks: z.array(z.object({ id, title: z.string(), containerId: id.nullable(), done: z.boolean() })),
  users: z.array(z.object({ id, name: z.string() })),
})
export type JumpIndex = z.infer<typeof JumpIndex>

// ── Favorites ─────────────────────────────────────────────────────────────────────────────────
export const Favorite = z.object({
  entityType: z.enum(['note', 'container']),
  entityId: id,
  title: z.string(),
})

// ── Attachments ───────────────────────────────────────────────────────────────────────────────
export const Attachment = z.object({
  id,
  filename: z.string(),
  mime: z.string(),
  size: z.number().int(),
  url: z.string(),
})

// ── Events (SSE /api/v1/events) ───────────────────────────────────────────────────────────────
export const ChangeEvent = z.object({
  table: z.enum([
    'containers',
    'container_members',
    'notes',
    'tasks',
    'inbox_items',
    'links',
    'people',
    'meetings',
    'decisions',
    'commitments',
    'notifications',
  ]),
  op: z.enum(['insert', 'update', 'delete']),
  id: z.string().nullable(),
  containerId: z.string().nullable(),
})
export type ChangeEvent = z.infer<typeof ChangeEvent>
